import { FormEvent, useEffect, useState } from 'react';
import { rejectCandidate } from '../../../../network/ats';
import type { PipelineCandidate } from './CandidateFormDialog';
import {
  Modal,
  errorMessage,
  fieldClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
} from './Modal';

type Props = {
  candidate: PipelineCandidate | null;
  onClose: () => void;
  onDone: (emailed: boolean) => void;
};

export const RejectCandidateDialog = ({ candidate, onClose, onDone }: Props) => {
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setReason('');
    setNotify(false);
    setError(null);
  }, [candidate]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!candidate) return;
    setIsSaving(true);
    setError(null);
    try {
      const result = await rejectCandidate(candidate.id, {
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        notifyCandidate: notify,
      });
      onDone(Boolean(result?.emailed));
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Could not reject the candidate.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={Boolean(candidate)}
      onClose={onClose}
      title={`Reject ${candidate?.name ?? 'candidate'}`}
      description="The reason is kept on the candidate's record for your team."
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <div>
          <label htmlFor="reject-reason" className={labelClass}>
            Reason (optional, internal)
          </label>
          <textarea
            id="reject-reason"
            rows={3}
            className={fieldClass}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={notify}
            disabled={!candidate?.email}
            onChange={(event) => setNotify(event.target.checked)}
          />
          {candidate?.email
            ? `Email ${candidate.email} that we are not moving forward`
            : 'No email address on file, so the candidate cannot be emailed'}
        </label>
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            className={secondaryButtonClass}
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button type="submit" className={primaryButtonClass} disabled={isSaving}>
            {isSaving ? 'Rejecting…' : 'Reject candidate'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
